import { useMemo, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Check, ArrowRight, Loader2, ShieldCheck, Upload, ChevronDown, ChevronUp } from "lucide-react";
import { toast } from "sonner";
import { EvidenceUploadDialog } from "@/components/governance/EvidenceUploadDialog";

import { supabase } from "@/integrations/supabase/client";
import { useMemberUna } from "@/hooks/useMemberUna";
import {
  RULE_GROUP_ORDER,
  RULE_GROUP_LABELS,
  STATUS_LABEL,
  STATUS_CLASS,
  getActionForRule,
  scoreStatusLine,
  type ItemStatus,
} from "@/lib/governanceHealth";
import { formatDateString } from "@/lib/utils";

const PERIOD_YEAR = 2026;

type Row = {
  id: string;
  rule_key: string;
  status: ItemStatus;
  due_date: string | null;
  satisfied_at: string | null;
  satisfied_by_type: string | null;
  na_reason: string | null;
  rule: {
    title: string;
    member_description: string | null;
    source_reference: string | null;
    rule_group: string;
    weight: number;
    member_can_mark_na: boolean;
    sort_order: number;
    evidence_types: string[] | null;
  };
};

export const GovernanceHealthContent = () => {
  const queryClient = useQueryClient();
  const { unaId, isLoading: unaLoading } = useMemberUna();

  const [naItem, setNaItem] = useState<Row | null>(null);
  const [naReason, setNaReason] = useState("");
  const [submittingNa, setSubmittingNa] = useState(false);
  const [uploadItem, setUploadItem] = useState<Row | null>(null);
  const [showDone, setShowDone] = useState(false);

  const { data: score } = useQuery({
    queryKey: ["gh-score", unaId, PERIOD_YEAR],
    queryFn: async () => {
      const { data } = await supabase
        .from("una_governance_scores")
        .select("score, satisfied_count, applicable_count, computed_at")
        .eq("una_id", unaId!)
        .eq("period_year", PERIOD_YEAR)
        .maybeSingle();
      return data;
    },
    enabled: !!unaId,
  });

  const { data: items = [], isLoading: itemsLoading } = useQuery({
    queryKey: ["gh-items", unaId, PERIOD_YEAR],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("una_compliance_items")
        .select(
          "id, rule_key, status, due_date, satisfied_at, satisfied_by_type, na_reason, rule:compliance_rule_catalog!inner(title, member_description, source_reference, rule_group, weight, member_can_mark_na, sort_order, evidence_types)"
        )
        .eq("una_id", unaId!)
        .or(`period_year.eq.${PERIOD_YEAR},period_year.is.null`);
      if (error) throw error;
      return (data || []) as unknown as Row[];
    },
    enabled: !!unaId,
  });

  const { openItems, doneItems } = useMemo(() => {
    const open: Row[] = [];
    const done: Row[] = [];
    for (const r of items) {
      if (r.status === "satisfied" || r.status === "not_applicable") done.push(r);
      else open.push(r);
    }
    const statusRank: Record<string, number> = { overdue: 0, due_soon: 1, upcoming: 2 };
    const groupOrder: Record<string, number> = Object.fromEntries(
      RULE_GROUP_ORDER.map((g, i) => [g, i]),
    );
    open.sort((a, b) => {
      const sa = statusRank[a.status] ?? 3;
      const sb = statusRank[b.status] ?? 3;
      if (sa !== sb) return sa - sb;
      const ga = groupOrder[a.rule.rule_group] ?? 99;
      const gb = groupOrder[b.rule.rule_group] ?? 99;
      if (ga !== gb) return ga - gb;
      return (b.rule.weight ?? 0) - (a.rule.weight ?? 0);
    });
    done.sort((a, b) => {
      const ga = groupOrder[a.rule.rule_group] ?? 99;
      const gb = groupOrder[b.rule.rule_group] ?? 99;
      if (ga !== gb) return ga - gb;
      return a.rule.sort_order - b.rule.sort_order;
    });
    return { openItems: open, doneItems: done };
  }, [items]);

  const openNa = (row: Row) => {
    setNaItem(row);
    setNaReason("");
  };

  const submitNa = async () => {
    if (!naItem || !naReason.trim()) return;
    setSubmittingNa(true);
    try {
      const { error } = await supabase.rpc("mark_item_not_applicable", {
        _item_id: naItem.id,
        _reason: naReason.trim(),
      });
      if (error) throw error;

      await supabase.rpc("compute_una_governance_score", {
        _una_id: unaId,
        _period_year: PERIOD_YEAR,
      });

      toast.success("Marked as not applicable");
      setNaItem(null);
      setNaReason("");
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: ["gh-items", unaId, PERIOD_YEAR] }),
        queryClient.invalidateQueries({ queryKey: ["gh-score", unaId, PERIOD_YEAR] }),
        queryClient.invalidateQueries({ queryKey: ["gh-next-items", unaId] }),
      ]);
    } catch (e: any) {
      toast.error(e?.message || "Could not update this item");
    } finally {
      setSubmittingNa(false);
    }
  };

  if (unaLoading) {
    return (
      <div className="py-12 text-center text-muted-foreground">Loading…</div>
    );
  }

  if (!unaId) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Governance Health</CardTitle>
          <CardDescription>
            Your governance record will appear here once your UNA is set up.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Button asChild>
            <Link to="/journey">Continue My Journey</Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  const scoreValue = score?.score ?? null;
  const applicable = score?.applicable_count ?? 0;
  const satisfied = score?.satisfied_count ?? 0;
  const allClear = applicable > 0 && satisfied >= applicable;

  const renderItemRow = (row: Row) => {
    const status = row.status as ItemStatus;
    const action = getActionForRule(
      row.rule_key,
      row.rule.rule_group,
      row.id,
      row.rule.evidence_types,
    );
    const canMarkNa =
      row.rule.member_can_mark_na && status !== "satisfied" && status !== "not_applicable";
    const canUpload =
      (row.rule.evidence_types || []).includes("upload") &&
      status !== "satisfied" && status !== "not_applicable";
    return (
      <div
        key={row.id}
        className="rounded-lg border p-4 flex flex-col md:flex-row md:items-start md:justify-between gap-3"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-start gap-2">
            {status === "satisfied" && (
              <Check className="w-4 h-4 text-emerald-600 mt-1 shrink-0" />
            )}
            <div className="flex-1">
              <div className="font-medium">{row.rule.title}</div>
              {row.rule.member_description && (
                <p className="text-sm text-muted-foreground mt-1">
                  {row.rule.member_description}
                </p>
              )}
              <div className="flex items-center gap-2 flex-wrap mt-2 text-xs text-muted-foreground">
                {row.rule.source_reference && (
                  <span>{row.rule.source_reference}</span>
                )}
                {row.due_date && (
                  <span>· Due {formatDateString(row.due_date)}</span>
                )}
                <Badge
                  variant="outline"
                  className={`ml-1 ${STATUS_CLASS[status]}`}
                >
                  {STATUS_LABEL[status]}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {RULE_GROUP_LABELS[row.rule.rule_group] || row.rule.rule_group}
                </Badge>
              </div>
              {status === "satisfied" && row.satisfied_at && (
                <p className="text-xs text-emerald-700 mt-1">
                  Satisfied {formatDateString(row.satisfied_at)}
                  {row.satisfied_by_type ? ` · ${row.satisfied_by_type}` : ""}
                </p>
              )}
              {status === "not_applicable" && row.na_reason && (
                <p className="text-xs text-muted-foreground mt-1 italic">
                  Marked not applicable: {row.na_reason}
                </p>
              )}
            </div>
          </div>
        </div>
        <div className="flex flex-col items-stretch md:items-end gap-2 md:min-w-[180px]">
          {status !== "satisfied" && status !== "not_applicable" && (
            <Button asChild size="sm" variant="default">
              <Link to={action.href}>
                {action.label}
                <ArrowRight className="w-3 h-3 ml-1" />
              </Link>
            </Button>
          )}
          {canUpload && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setUploadItem(row)}
            >
              <Upload className="w-3 h-3 mr-1" />
              Upload Evidence
            </Button>
          )}
          {canMarkNa && (
            <Button
              size="sm"
              variant="ghost"
              className="text-xs text-muted-foreground"
              onClick={() => openNa(row)}
            >
              Doesn't apply to me
            </Button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      

      {/* Score header */}
      <Card className="border-gold/30">
        <CardHeader>
          <div className="flex items-center gap-2">
            <ShieldCheck className="w-5 h-5 text-gold" />
            <CardTitle className="text-2xl">Governance Health</CardTitle>
          </div>
          <CardDescription>{scoreStatusLine(scoreValue)}</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-6 flex-wrap">
            <div className="flex flex-col items-center justify-center rounded-full border-4 border-gold/40 w-28 h-28">
              <span className="text-5xl font-semibold text-charcoal leading-none">
                {scoreValue ?? "—"}
              </span>
              <span className="text-xs text-muted-foreground mt-1">of 100</span>
            </div>
            <div className="text-sm">
              <div className="font-medium text-foreground text-base">
                {satisfied} of {applicable} satisfied
              </div>
              <div className="text-muted-foreground mt-1">2026 record</div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Open items — the centerpiece */}
      {itemsLoading ? (
        <div className="text-center text-muted-foreground py-8">Loading requirements…</div>
      ) : openItems.length === 0 ? (
        <Card className="border-emerald-200 bg-emerald-50/40">
          <CardHeader>
            <CardTitle className="text-lg text-emerald-800">
              {allClear
                ? "Everything is up to date"
                : "Nothing needs your attention right now"}
            </CardTitle>
            <CardDescription>
              {allClear
                ? "Your governance record is fully satisfied for this period. The tools below stay ready when you need them."
                : "New requirements will appear here as they come due. The tools below stay ready in the meantime."}
            </CardDescription>
          </CardHeader>
        </Card>
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">What needs your attention</CardTitle>
            <CardDescription>
              Each item links straight into the tool that closes it.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-3">
            {openItems.map(renderItemRow)}
          </CardContent>
        </Card>
      )}

      {/* Done — quiet collapsed section */}
      {doneItems.length > 0 && (
        <Card>
          <CardHeader
            className="cursor-pointer select-none"
            onClick={() => setShowDone((v) => !v)}
          >
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-base">Done ({doneItems.length})</CardTitle>
                <CardDescription>
                  Items already satisfied or marked not applicable.
                </CardDescription>
              </div>
              {showDone ? (
                <ChevronUp className="w-4 h-4 text-muted-foreground" />
              ) : (
                <ChevronDown className="w-4 h-4 text-muted-foreground" />
              )}
            </div>
          </CardHeader>
          {showDone && (
            <CardContent className="space-y-3">
              {doneItems.map(renderItemRow)}
            </CardContent>
          )}
        </Card>
      )}

      <Dialog open={!!naItem} onOpenChange={(o) => !o && setNaItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Mark not applicable</DialogTitle>
            <DialogDescription>
              Share a brief reason this requirement doesn't apply to your UNA.
              This is recorded with your governance record.
            </DialogDescription>
          </DialogHeader>
          <Textarea
            value={naReason}
            onChange={(e) => setNaReason(e.target.value)}
            placeholder="e.g., We don't hold a bank account under this UNA."
            rows={4}
          />
          <DialogFooter>
            <Button variant="ghost" onClick={() => setNaItem(null)} disabled={submittingNa}>
              Cancel
            </Button>
            <Button onClick={submitNa} disabled={!naReason.trim() || submittingNa}>
              {submittingNa && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {unaId && (
        <EvidenceUploadDialog
          open={!!uploadItem}
          onOpenChange={(o) => !o && setUploadItem(null)}
          unaId={unaId}
          mode="evidence"
          complianceItem={
            uploadItem
              ? { id: uploadItem.id, title: uploadItem.rule.title }
              : undefined
          }
        />
      )}
    </div>
  );
};

export default GovernanceHealthContent;
